/**
 * TrendCard - Trend Direction & Score Indicator
 *
 * Displays: Overall trend, trend score, trend strength
 *
 * @version 1.0.0
 */

import React, { memo } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import IndicatorCard from '../IndicatorCard';

const TrendCard = memo(({ overallTrend, trendScore, trendAnalysis }) => {
    const getTrendColor = (trend) => {
        if (!trend) return '#71717A';
        const t = trend.toUpperCase();
        if (t.includes('STRONG_UP')) return '#22c55e';
        if (t.includes('UP')) return '#4ade80';
        if (t.includes('STRONG_DOWN')) return '#ef4444';
        if (t.includes('DOWN')) return '#f87171';
        return '#fbbf24'; // RANGING/SIDEWAYS
    };

    const getTrendArrow = (trend) => {
        if (!trend) return '→';
        const t = trend.toUpperCase();
        if (t.includes('UP')) return '↑';
        if (t.includes('DOWN')) return '↓';
        return '→';
    };

    const getTrendStatus = (trend) => {
        if (!trend) return 'neutral';
        const t = trend.toUpperCase();
        if (t.includes('UP')) return 'bullish';
        if (t.includes('DOWN')) return 'bearish';
        return 'neutral';
    };

    const trendColor = getTrendColor(overallTrend);
    const score = trendScore || 0;

    const summary = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* Trend Direction */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                    sx={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: trendColor
                    }}
                >
                    {getTrendArrow(overallTrend)}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: trendColor,
                        textTransform: 'uppercase'
                    }}
                >
                    {overallTrend?.replace('_', ' ') || 'N/A'}
                </Typography>
            </Box>

            {/* Score */}
            <Box
                sx={{
                    backgroundColor: `${trendColor}20`,
                    color: trendColor,
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                }}
            >
                {score}/100
            </Box>
        </Box>
    );

    return (
        <IndicatorCard
            title="TREND"
            icon="📈"
            summary={summary}
            status={getTrendStatus(overallTrend)}
            defaultExpanded={false}
        >
            {/* Expanded: Trend breakdown by timeframe */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Score Progress Bar */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '10px', color: '#71717A' }}>
                            Trend Strength
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: trendColor, fontWeight: 600 }}>
                            {score}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={score}
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: trendColor,
                                borderRadius: 2
                            }
                        }}
                    />
                </Box>

                {/* Timeframe breakdown */}
                {trendAnalysis && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {['h4', 'd1'].map((tf) => {
                            const tfData = trendAnalysis[tf];
                            if (!tfData) return null;
                            const tfTrend = tfData.trend || tfData.direction;
                            const tfColor = getTrendColor(tfTrend);

                            return (
                                <Box
                                    key={tf}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '4px 8px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                        borderRadius: '4px'
                                    }}
                                >
                                    <Typography sx={{ fontSize: '10px', color: '#A1A1AA', fontFamily: 'monospace' }}>
                                        {tf.toUpperCase()}
                                    </Typography>
                                    <Typography sx={{ fontSize: '10px', color: tfColor, fontWeight: 600 }}>
                                        {getTrendArrow(tfTrend)} {tfTrend?.replace('_', ' ') || 'N/A'}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </IndicatorCard>
    );
});

TrendCard.displayName = 'TrendCard';

export default TrendCard;
